import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DriverTable } from "../components/DriverTable";
import { DriverToolbar } from "../components/DriverToolbar";
import SubscriptionUpgradeModal from "../components/SubscriptionUpgradeModal";
import TransporterInfoForm from "../components/TransporterInfoForm";
import { useDriverApi } from "../hooks/useDriverApi";
import { useTransporterInfo } from "../hooks/useTransporterInfo";
import { ColumnDefinition, Driver } from "../types/driver.types";
import { getItem, setItem } from "../utils/localStorage";
import { createToastMessage } from "../utils/toastUtils";

import DriverForm from "../components/DriverForm";
import {
  SubscriptionType,
  Transporter,
} from "../interfaces/transporters.interface";
import "./DriversManagment.css";

const VISIBLE_COLUMNS_KEY = "driverManagement_visibleColumns";
const FREE_TRIAL_DRIVER_LIMIT = 5; // 5 drivers for free trial

const DriversManagement: React.FC = () => {
  const {
    drivers,
    isLoading: isDriversLoading,
    fetchDrivers,
    addDriver,
    updateDriver,
    deleteDriver,
    resetDriverPassword,
  } = useDriverApi();
  const {
    isAccountComplete,
    transporterInfo,
    updateTransporterInfo,
    isLoading: isTransporterInfoLoading,
    error: transporterInfoError,
  } = useTransporterInfo();

  const [selectedDrivers, setSelectedDrivers] = useState<Driver[]>([]);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [driverDialogVisible, setDriverDialogVisible] = useState(false);
  const [deleteDriverDialogVisible, setDeleteDriverDialogVisible] =
    useState(false);
  const [deleteDriversDialogVisible, setDeleteDriversDialogVisible] =
    useState(false);
  const [transporterInfoFormVisible, setTransporterInfoFormVisible] =
    useState(false);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const toast = useRef<Toast>(null);
  const dt = useRef<any>(null);
  const isTrial =
    transporterInfo?.subscriptionType === SubscriptionType.FREE_TRIAL;

  const [columns] = useState<ColumnDefinition[]>([
    { field: "drivingLicenseNumber", header: "N° Permis" },
    { field: "drivingLicenseType", header: "Type de Permis" },
    { field: "name", header: "Nom" },
    { field: "lastName", header: "Prénom" },
    { field: "email", header: "Email" },
    { field: "phone", header: "Téléphone" },
    { field: "civility", header: "Civilité" },
    { field: "creationDate", header: "Date de création" },
    { field: "birthDate", header: "Date de naissance" },
    { field: "manuallyActive", header: "Statut" },
  ]);

  const [visibleColumns, setVisibleColumns] = useState<ColumnDefinition[]>(
    () => {
      const savedColumns = getItem<string[]>(VISIBLE_COLUMNS_KEY);
      if (savedColumns) {
        return columns.filter((col) => savedColumns.includes(col.field));
      }
      return columns;
    }
  );

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    const visibleFields = visibleColumns.map((col) => col.field);
    setItem(VISIBLE_COLUMNS_KEY, visibleFields);
  }, [visibleColumns]);

  const getDriverLimit = useCallback(() => {
    if (!transporterInfo) return FREE_TRIAL_DRIVER_LIMIT;

    switch (transporterInfo.subscriptionType) {
      case SubscriptionType.FREE_TRIAL:
        return FREE_TRIAL_DRIVER_LIMIT;
      case SubscriptionType.AFFILIATE:
        // If they have claimed the unlimited reward, no limit
        if (transporterInfo.hasClaimedAffiliateReward) {
          return Infinity;
        }
        // Otherwise, base limit (5) + 1 for first referral if they have any
        return (
          FREE_TRIAL_DRIVER_LIMIT +
          (transporterInfo.referredTransporters?.length ? 1 : 0)
        );
      case SubscriptionType.PAID:
        return Infinity;
      default:
        return FREE_TRIAL_DRIVER_LIMIT;
    }
  }, [transporterInfo]);

  const hasReachedDriverLimit = useCallback(() => {
    if (!drivers) return false;
    const limit = getDriverLimit();
    return drivers.length >= limit;
  }, [drivers, getDriverLimit]);

  const isSubscriptionExpired = useCallback(() => {
    return (
      transporterInfo?.endDate && new Date(transporterInfo.endDate) < new Date()
    );
  }, [transporterInfo]);

  const openNew = () => {
    if (!isAccountComplete) {
      setTransporterInfoFormVisible(true);
      return;
    }

    if (hasReachedDriverLimit() || isSubscriptionExpired()) {
      setUpgradeModalVisible(true);
      return;
    }

    setDriver(null);
    setDriverDialogVisible(true);
  };

  const hideDialog = () => {
    setDriverDialogVisible(false);
  };

  const hideDeleteDriverDialog = () => {
    setDeleteDriverDialogVisible(false);
  };

  const hideDeleteDriversDialog = () => {
    setDeleteDriversDialogVisible(false);
  };

  const hideTransporterInfoForm = () => {
    setTransporterInfoFormVisible(false);
  };

  const hideUpgradeModal = () => {
    setUpgradeModalVisible(false);
  };

  const editDriver = (driver: Driver) => {
    setDriver({ ...driver });
    setDriverDialogVisible(true);
  };

  const confirmDeleteDriver = (driver: Driver) => {
    setDriver(driver);
    setDeleteDriverDialogVisible(true);
  };

  const confirmDeleteSelected = () => {
    setDeleteDriversDialogVisible(true);
  };

  const handleSaveDriver = async (
    driverData: Partial<Driver>
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (driver && driver._id) {
        await updateDriver(driver._id, driverData);
      } else {
        await addDriver(driverData as Omit<Driver, "_id" | "password">);
      }
      await fetchDrivers();
      setDriverDialogVisible(false);
      toast.current?.show(
        createToastMessage("success", "Succès", "Chauffeur enregistré")
      );
      return { success: true };
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Échec de l'enregistrement du chauffeur";
      toast.current?.show(createToastMessage("error", "Erreur", errorMessage));
      return { success: false, error: errorMessage };
    }
  };

  const handleDeleteDriver = async () => {
    if (driver && driver._id) {
      try {
        await deleteDriver(driver._id);
        setDriver(null);
        setDeleteDriverDialogVisible(false);
        await fetchDrivers();
        toast.current?.show(
          createToastMessage("success", "Succès", "Chauffeur supprimé")
        );
      } catch (err: any) {
        const errorMessage =
          err.message || "Échec de la suppression du chauffeur";
        toast.current?.show(
          createToastMessage("error", "Erreur", errorMessage)
        );
      }
    }
  };

  const handleDeleteSelectedDrivers = async () => {
    try {
      await Promise.all(
        selectedDrivers
          .filter((driver) => driver._id)
          .map((driver) => deleteDriver(driver._id!))
      );
      setSelectedDrivers([]);
      setDeleteDriversDialogVisible(false);
      await fetchDrivers();
      toast.current?.show(
        createToastMessage("success", "Succès", "Chauffeurs supprimés")
      );
    } catch (err: any) {
      const errorMessage =
        err.message || "Échec de la suppression des chauffeurs";
      toast.current?.show(createToastMessage("error", "Erreur", errorMessage));
    }
  };

  const handleResetPassword = async (driverId: string) => {
    try {
      await resetDriverPassword(driverId);
      toast.current?.show(
        createToastMessage(
          "success",
          "Succès",
          "Mot de passe du chauffeur réinitialisé"
        )
      );
    } catch (err: any) {
      const errorMessage =
        err.message ||
        "Échec de la réinitialisation du mot de passe du chauffeur";
      toast.current?.show(createToastMessage("error", "Erreur", errorMessage));
    }
  };

  const handleUpdateTransporterInfo = async (newInfo: Partial<Transporter>) => {
    try {
      await updateTransporterInfo(newInfo);
      setTransporterInfoFormVisible(false);
      toast.current?.show(
        createToastMessage(
          "success",
          "Succès",
          "Informations du transporteur mises à jour"
        )
      );
    } catch (err: any) {
      const errorMessage =
        err.message ||
        "Échec de la mise à jour des informations du transporteur";
      toast.current?.show(createToastMessage("error", "Erreur", errorMessage));
    }
  };

  const exportCSV = () => {
    dt.current.exportCSV();
  };

  const onColumnToggle = (event: { value: ColumnDefinition[] }) => {
    setVisibleColumns(event.value);
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-70px)] flex flex-col p-4 sm:p-8">
      <Toast ref={toast} />

      <div className="card flex-grow flex flex-col">
        <DriverToolbar
          onAdd={openNew}
          onDeleteSelected={confirmDeleteSelected}
          onExport={exportCSV}
          hasSelection={selectedDrivers.length > 0}
        />

        <div className="flex justify-end mt-4 mb-2">
          <MultiSelect
            value={visibleColumns}
            options={columns}
            optionLabel="header"
            onChange={onColumnToggle}
            className="w-full sm:w-auto rounded-md font-sans"
            placeholder="Colonnes visibles"
            showSelectAll
            selectedItemsLabel={`{0}/${columns.length} colonnes visibles`}
          />
        </div>

        <DriverTable
          drivers={drivers}
          selectedDrivers={selectedDrivers}
          onSelectionChange={(e) => setSelectedDrivers(e.value)}
          onEdit={editDriver}
          onDelete={confirmDeleteDriver}
          onResetPassword={handleResetPassword}
          columns={visibleColumns}
          isLoading={isDriversLoading}
        />
      </div>

      <DriverForm
        visible={driverDialogVisible}
        onHide={hideDialog}
        onSave={handleSaveDriver}
        driver={driver}
        isTrial={isTrial}
      />

      <ConfirmDialog
        visible={deleteDriverDialogVisible}
        onHide={hideDeleteDriverDialog}
        message="Êtes-vous sûr de vouloir supprimer ce chauffeur ?"
        header="Confirmer"
        icon="pi pi-exclamation-triangle"
        accept={handleDeleteDriver}
        acceptLabel="Oui"
        rejectLabel="Non"
      />

      <ConfirmDialog
        visible={deleteDriversDialogVisible}
        onHide={hideDeleteDriversDialog}
        message="Êtes-vous sûr de vouloir supprimer les chauffeurs sélectionnés ?"
        header="Confirmer"
        icon="pi pi-exclamation-triangle"
        accept={handleDeleteSelectedDrivers}
        acceptLabel="Oui"
        rejectLabel="Non"
      />

      <Dialog
        visible={transporterInfoFormVisible}
        onHide={hideTransporterInfoForm}
        header="Compléter les informations du compte"
        modal
        className="font-sans text-md rounded-xl overflow-hidden shadow-lg bg-white p-6 w-full max-w-2xl mx-auto"
        contentClassName="p-4"
      >
        <p className="mb-4 text-gray-700">
          Veuillez compléter les informations manquantes de votre compte avant
          d'ajouter un nouveau chauffeur.
        </p>
        <TransporterInfoForm
          onSubmit={handleUpdateTransporterInfo}
          initialData={transporterInfo}
          isLoading={isTransporterInfoLoading}
          error={transporterInfoError}
          onUpgradeSubscription={() => {}}
        />
      </Dialog>

      <SubscriptionUpgradeModal
        visible={upgradeModalVisible}
        onHide={hideUpgradeModal}
        isExpired={isSubscriptionExpired()}
      />
    </div>
  );
};

export default DriversManagement;
